import { ApiError, apiRequest, redirectToLogin } from './apiClient.js';
import type { AuthStatus } from '../types/auth.js';
import { getMarketingLoginUrl } from '../utils/marketingSite.js';
import {
  clearAuthStatusCache,
  getCachedAuthStatus,
  setCachedAuthStatus,
} from './authStatusCache.js';
import { coalesceRequest } from './requestCoalescer.js';
import { setEffectiveSearchQueryMinLength } from './searchService.js';
import { setEffectiveChatMessageMaxLength } from './chatLimits.js';

export type { AuthStatus, AuthUser } from '../types/auth.js';

export { clearAuthStatusCache } from './authStatusCache.js';

export type GetAuthStatusOptions = {
  /** When true, always hit the server (session guards, post-mutation refresh). */
  force?: boolean;
};

export type AuthSessionProbe = {
  authenticated: boolean;
};

/** Lightweight session check for the auth gate — no policy DB lookups. */
export async function probeAuthSession(): Promise<AuthSessionProbe> {
  return coalesceRequest('auth:session', () =>
    apiRequest<AuthSessionProbe>('/api/auth/session')
  );
}

export async function getAuthStatus(options?: GetAuthStatusOptions): Promise<AuthStatus> {
  if (!options?.force) {
    const cached = getCachedAuthStatus();
    if (cached) {
      return cached;
    }
  }

  const cacheKey = options?.force ? 'auth:status:force' : 'auth:status';

  try {
    return await coalesceRequest(cacheKey, async () => {
      const status = await apiRequest<AuthStatus>('/api/auth/status');
      setCachedAuthStatus(status);
      // The search minimum is server-configured (#384). Adopt whatever this
      // deployment runs so the UI never sends a query the server would 400 —
      // hard-coding it broke as soon as an operator RAISED the value.
      setEffectiveSearchQueryMinLength(status.searchQueryMinLength);
      // Same reasoning for the composer's length guard (#452): the cap is
      // server-configured, so adopt it rather than hard-coding a second copy
      // that a lowered server limit would silently invalidate.
      setEffectiveChatMessageMaxLength(status.chatMessageMaxLength);
      return status;
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      clearAuthStatusCache();
      redirectToLogin();
      return { authenticated: false };
    }
    throw err;
  }
}

export function buildGoogleLoginUrl(): string {
  const redirectOrigin = encodeURIComponent(window.location.origin);
  return `/auth/google?redirect=${redirectOrigin}`;
}

export async function isAuthenticated(): Promise<boolean> {
  const status = await probeAuthSession();
  return status.authenticated;
}

export function getCurrentUser(): AuthStatus['user'] | null {
  return getCachedAuthStatus()?.user ?? null;
}

export async function logout(): Promise<void> {
  // POST (not a GET navigation) so logout cannot be triggered cross-site (CSRF).
  // Same-origin fetch carries Origin/credentials, satisfying the CSRF + session
  // checks. The server returns the post-logout redirect target.
  let redirect = getMarketingLoginUrl();
  try {
    const result = await apiRequest<{ ok: boolean; redirect?: string }>('/auth/logout', {
      method: 'POST',
    });
    if (result?.redirect) {
      redirect = result.redirect;
    }
  } catch {
    // Fall through to the default redirect even if the request failed; the
    // session cookie is httpOnly so we cannot clear it client-side.
  }
  clearAuthStatusCache();
  window.location.href = redirect;
}

// #398: `isGoogleOAuthRequired()` and `isBypassAuthEnabled()` used to live here.
// They read `import.meta.env.VITE_BYPASS_AUTH` — a value Vite INLINES into the
// shipped client bundle — so an auth-bypass switch was compiled into a public
// asset. Both were already dead (exported from services/index.ts, called
// nowhere), and auth is enforced by the backend regardless, which is why this was
// never exploitable. Deleted rather than gated behind `import.meta.env.DEV`:
// there is no client-side auth decision to make. `vite.config.ts` now fails any
// build that has `VITE_BYPASS_AUTH` set, so it cannot come back by accident.
